import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { catchError, finalize } from 'rxjs';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { ToastrService } from 'ngx-toastr';
import { FileUploadService } from './file-upload.service';
import { TableModule } from 'primeng/table';
import { ContentHeaderEditComponent } from '../content-header-edit/content-header-edit.component';
import { DropdownModel, EditComponent, ViewComponent } from '../../../core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { fileUploadForm, noWhitespaceValidator } from './forms/file-upload.form';
import { FloatLabelModule } from 'primeng/floatlabel';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DropdownModule } from 'primeng/dropdown';
import { DocumentTypeService } from '../../../services/document-type.service';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { typeDropdown } from '../../consts/type-dropdown';
import { ValidationFeedbackComponent } from '../validation-feedback';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TableModule,
    ContentHeaderEditComponent,
    FloatLabelModule,
    ReactiveFormsModule,
    DropdownModule,
    InputTextModule,
    TooltipModule,
    ViewComponent,
    ToastModule,
    ValidationFeedbackComponent,
    FormsModule,
    InputTextModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileUploadComponent extends EditComponent {
  override id = input<string>();
  typeID = signal(this.formGroup.controls['typeID']);
  document = signal(this.formGroup.controls['document']);
  documentID = signal(this.formGroup.controls['documentID']);
  documentName = signal(this.formGroup.controls['documentName']);
  documentPath = signal(this.formGroup.controls['documentPath']);
  documentType = signal(this.formGroup.controls['documentType']);
  quoteID = signal(this.formGroup.controls['quoteID']);
  customerID = signal(this.formGroup.controls['customerID']);
  documentsTypes = signal<DropdownModel[]>([]);
  type = signal(this.formGroup.controls['type']);
  options = signal(typeDropdown());
  uploading = signal(false);
  fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');
  isDragging = signal(false);

  private _destroyRef = inject(DestroyRef);
  private _errorHandlingService = inject(ErrorHandlingService);
  private _toastr = inject(ToastrService);

  constructor(
    public override route: ActivatedRoute,
    public override router: Router,
    public override messageService: MessageService,
    public fileUploadService: FileUploadService,
    private _documentTypeService: DocumentTypeService
  ) {
    super(fileUploadService, route, router, fileUploadForm(), messageService);
  }

  ngOnInit() {
    this.getDocumentType();
    this.typeID().valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        if (+value === 7) {
          this.type().setValidators([Validators.required, noWhitespaceValidator]);
        } else {
          this.type().clearValidators();
        }
        this.type().updateValueAndValidity();
      });
  }

  override handleSave(): void {
    if (this.uploading()) return;

    const typeId = this.typeID().value;
    const file = this.document().value as File | null;
    const documentId = this.documentID().value;

    if (!typeId || (!file && !documentId)) return;

    const formData = this.buildFormData(file);

    this.uploading.set(true);
    this.formGroup.disable({ emitEvent: false });
    this.service
      .save(formData)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        catchError((err) => this._errorHandlingService.handleError(err, 'Error uploading file')),
        finalize(() => {
          this.uploading.set(false);
          this.formGroup.enable({ emitEvent: false });
          this.changeDetectorRef.markForCheck?.();
        }),
      )
      .subscribe({
        next: () => {
          this.toastr.success('File uploaded successfully', 'Success');
          this.cancel(true);
        },
        error: () => {},
      });
  }

  override hasPermissionEdit() {
    return this.tokenService.isAdmin() || this.tokenService.isContributor();
  }

  triggerFileInput() {
    if (!this.uploading()) {
      this.fileInput()?.nativeElement?.click();
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.uploading()) this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging.set(false);
    if (this.uploading()) return;
    const files = event.dataTransfer?.files;
    if (files?.length) this.handleFileSelection(files[0]);
  }

  onSelect({ target }: Event & { target: any }) {
    const file = target.files[0];
    if (file) this.handleFileSelection(file);
  }

  onRemove() {
    if (this.uploading()) return;
    const documentId = this.documentID().value;
    if (!documentId) {
      this.document().patchValue(null);
      return;
    }
    this.fileUploadService
      .deleteFile(documentId)
      .pipe(
        catchError((err) => this._errorHandlingService.handleError(err, 'Error deleting file')),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(() => {
        this.documentID().patchValue(null);
        this.documentName().patchValue(null);
        this.documentPath().patchValue(null);
        this.documentType().patchValue(null);
        this.document().patchValue(null);
        this.changeDetectorRef.detectChanges();
        this._toastr.success('File deleted successfully', 'Success');
      });
  }

  getDocumentType() {
    this._documentTypeService
      .getDropdown()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: DropdownModel[]) => this.documentsTypes.set(result));
  }

  private handleFileSelection(file: File): void {
    this.document().patchValue(file);
    this.changeDetectorRef.markForCheck?.();
  }

  private buildFormData(file: File | null): FormData {
    const fd = new FormData();
    this.appendIfPresent(fd, 'CustomerID', this.customerID().value);
    if (this.isGuid(this.quoteID().value)) {
      fd.append('QuoteID', String(this.quoteID().value).trim());
    }
    this.appendIfPresent(fd, 'DocumentID', this.documentID().value);
    if (file) {
      fd.append('Document', file);
      fd.append('DocumentName', file.name);
      fd.append('DocumentPath', file.name);
    } else {
      this.appendIfPresent(fd, 'DocumentName', this.documentName().value);
      this.appendIfPresent(fd, 'DocumentPath', this.documentPath().value ?? this.documentName().value);
    }
    fd.set('TypeID', String(this.typeID().value));
    if (+this.typeID().value === 7) {
      const t = this.type().value;
      if (typeof t === 'string' && t.trim()) fd.append('Type', t.trim());
    }
    return fd;
  }

  /** Backend QuoteID is Guid? — invalid strings break model binding before the controller runs. */
  private isGuid(value: unknown): boolean {
    if (value == null) return false;
    const s = String(value).trim();
    return !!s && s !== '--' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
  }

  private appendIfPresent(fd: FormData, key: string, value: unknown): void {
    if (value == null) return;
    const s = String(value).trim();
    if (s) fd.append(key, s);
  }
}
